import { useRef, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useCornerResize } from '@/hooks/useCornerResize';
import { PopoverResizeHandle } from '@/components/PopoverResizeHandle';

export interface PopoverShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wraps this element in Radix's Trigger — clicking it toggles `open`. Exactly one of
   *  `trigger` / `anchor` must be given. */
  trigger?: ReactElement;
  /** Wraps this element in Radix's Anchor instead of Trigger, for popovers whose open state
   *  is driven by a different click target than the anchor itself — e.g. RemovableTag's ×
   *  button lives inside the tag chip that is also the anchor. */
  anchor?: ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  collisionPadding?: number;
  /** Shadow weight — 'md' (default; most info/form popovers) or 'lg' (confirm/destructive
   *  actions, where a bit more visual weight is warranted). */
  shadow?: 'md' | 'lg';
  /** CSS width / min-width / max-width — any valid CSS length (number → px, or a string
   *  for things like `'min(560px, calc(100vw - 24px))'`). */
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  /** Renders a small triangle pointer toward the trigger/anchor (Radix Arrow). */
  arrow?: boolean;
  /** Adds a bottom-right drag handle (useCornerResize) so the user can grow/shrink the
   *  popover beyond its default width/maxHeight — only for popovers with a close button
   *  (an identity-card header), where a panel-like size adjustment makes sense; plain
   *  hover/click-outside-only popovers should leave this off. Only affects call sites that
   *  pass it — off by default. */
  resizable?: boolean;
  /** Floor for the drag — defaults to useCornerResize's own defaults (280×160). */
  resizeMinWidth?: number;
  resizeMinHeight?: number;
  /** Extra class appended to the Radix Content element. */
  className?: string;
  children: ReactNode;
}

/**
 * Shared chrome for every anchored "click near a trigger, get a floating card" popover in
 * this codebase — border/radius/shadow, `position: fixed` collision-aware placement, focus
 * trap, Esc-to-close, and outside-click-to-close all come from `@radix-ui/react-popover`
 * (already a dependency, already used in several places) instead of each popover hand-rolling
 * `getBoundingClientRect()` + scroll/resize listeners.
 *
 * Headerless/footerless by default — the identity-card header (thumbnail + name + ✕) that
 * `.est-price-popover-header` / `.campaign-history-popover` use is left to callers to render
 * as part of `children` (see InclusionCommentEditor/TagEditPopover/EmailCell), not baked into
 * the shell itself.
 *
 * `resizable` adds a bottom-right drag handle (useCornerResize) for popovers that have such a
 * close button — before the first drag it applies no width/height override at all, so the
 * popover's normal auto/content-driven sizing (and its `maxHeight`/`maxWidth` viewport-fit
 * ceiling) is untouched; dragging then pins an explicit width/height starting from whatever
 * box was actually on screen at that moment.
 */
export function PopoverShell({
  open,
  onOpenChange,
  trigger,
  anchor,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  collisionPadding = 12,
  shadow = 'md',
  width,
  minWidth,
  maxWidth,
  maxHeight,
  arrow = false,
  resizable = false,
  resizeMinWidth,
  resizeMinHeight,
  className,
  children,
}: PopoverShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { size, onResizeStart } = useCornerResize(contentRef, {
    minWidth: resizeMinWidth,
    minHeight: resizeMinHeight,
  });

  const style: CSSProperties = {
    width,
    minWidth,
    maxWidth,
    maxHeight,
    ...(size ? { width: size.width, height: size.height } : undefined),
    ...(resizable ? { position: 'relative' } : undefined),
    ...(shadow === 'lg' ? { boxShadow: 'var(--ggc-shadow-lg, 0 8px 12px 0 rgba(0, 0, 0, 0.1))' } : undefined),
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>}
      {anchor && <PopoverPrimitive.Anchor asChild>{anchor}</PopoverPrimitive.Anchor>}
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={contentRef}
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          // Popovers are frequently anchored inside a clickable row/card (a table row, a
          // tag chip) — without this, a click on the popover's own content (a button, a
          // field) bubbles up and re-triggers that outer handler (e.g. row navigation).
          onClick={(e) => e.stopPropagation()}
          className={`popover-shell${className ? ` ${className}` : ''}`}
          style={style}
        >
          {children}
          {resizable && <PopoverResizeHandle onPointerDown={onResizeStart} />}
          {arrow && <PopoverPrimitive.Arrow className="fill-white" width={12} height={6} />}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
